import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { GuidePanel, ContextHelpButton, OnboardingTour, shouldShowInitialTour } from "../components/guide/GuideSystem";
import { pageKeyByPath, type GuidePageKey } from "../config/guideContent";
import { Icon } from "../components/ui/Icon";
import { ActionButton, AlertCard, EmptyState, ErrorState, LoadingState, MetricCard, PageHeader, SearchInput, StatusPill } from "../components/operations/OperationalComponents";
import { CommandPalette } from "../components/command/CommandPalette";
import * as Legacy from "../app/runtime";

const { localPreviewPorts, apiBaseUrl, http, getUser, setAuth, clearAuth, getErr, fmt, fmtDate, fmtDateFull, fmtTime, fmtRel, urgColor, urgLabel, todayStr, startOfDayIso, endOfDayIso, genKey, safePhone, waUrl, mapsUrl, telUrl, openExternal, BRAND_LOGO, TOK, C, EXECUTIVE_ROLES, MANAGER_ROLES, SALES_ROLES, OPS_ROLES, ADMIN_ROLES, WHATSAPP_ROLES, FOLLOW_UP_ROLES, ROLE_LABELS, roleLabel, hasAnyRole, CSS, BrandLogo, ICON_NAMES, renderIcon, Btn, Input, Card, Modal, Toast, Skeleton, Badge, StatusBadge, KPI, SH, Tabs, Row, Empty, useToast, QO, useOrders, useOrder, useCustSrch, useCust, useNotifs, useRules, useCal, useMemory, useFollowUps, useRevenue, useRisks, useStaff, useFeedback, useAssist, useHealth, useUsers, useApprovals, useAudit, useDelivery, useInvoices, useMktRpt, useCharge, useSearch, useInquiries, useGST, useFollowUpTasks, useWhatsAppConversations, useWhatsAppConversation, useWhatsAppAssignableUsers, GlobalSearch, NotifBell, PIPE, StatusPipeline, CancelModal, RescheduleModal, RiderModal, OrderCommandCenter, NewOrderForm, OrderCard, LOST_REASONS, NewInquiryForm, InquiryConversationModal, QuickActions, NAV_MAP } = Legacy;

export const IntelDash = () => {
  const user = getUser();
  const role = user?.role || "";
  const canFollowUps = ["OWNER", "MASTER_ADMIN", "SUPER_ADMIN", "OPERATIONS_MANAGER", "SALES_MANAGER", "SALES"].includes(role);
  const canRevenue = ["OWNER", "MASTER_ADMIN", "SUPER_ADMIN", "OPERATIONS_MANAGER", "SALES_MANAGER", "ACCOUNTS"].includes(role);
  const canRisks = ["OWNER", "MASTER_ADMIN", "SUPER_ADMIN", "OPERATIONS_MANAGER", "SALES_MANAGER"].includes(role);
  const canStaff = ["OWNER", "MASTER_ADMIN", "SUPER_ADMIN"].includes(role);
  const canFeedback = ["OWNER", "MASTER_ADMIN", "SUPER_ADMIN", "OPERATIONS_MANAGER", "SALES_MANAGER", "MARKETING"].includes(role);
  const tabs = [
    canFollowUps && "Follow-ups",
    canRevenue && "Revenue",
    canRisks && "Risks",
    canStaff && "Staff",
    canFeedback && "Feedback",
  ].filter(Boolean) as string[];
  const [tab, setTab]  = useState(tabs[0] || "Feedback");
  useEffect(() => { if (!tabs.includes(tab)) setTab(tabs[0] || "Feedback"); }, [tabs.join("|"), tab]);
  const { data: fu, isLoading: fuL } = useFollowUps(canFollowUps && tab === "Follow-ups");
  const { data: rev }  = useRevenue(canRevenue && tab === "Revenue");
  const { data: risks }= useRisks(canRisks && tab === "Risks");
  const { data: staff }= useStaff(canStaff && tab === "Staff");
  const { data: fbk }  = useFeedback(canFeedback && tab === "Feedback");
  const { data: assist } = useAssist(user?.role || "SUPER_ADMIN");

  const rC = (s: string) => s === "CRITICAL" ? C.danger : s === "HIGH" ? C.warning : C.mid;
  const Bar = ({ label, value, max, color = C.primary }: any) => (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}><span style={{ color: C.mid }}>{label}</span><span style={{ fontWeight: 700 }}>{value}</span></div>
      <div style={{ height: 6, background: C.bg, borderRadius: 3, overflow: "hidden" }}><div style={{ width: `${max > 0 ? Math.min(100, typeof value === "number" ? (value / max) * 100 : 50) : 0}%`, height: "100%", background: color, borderRadius: 3, transition: "width .5s" }} /></div>
    </div>
  );

  return (
    <div>
      {/* AI assistant strip */}
      {(assist?.alerts || []).length > 0 && (
        <div style={{ background: `linear-gradient(135deg,${C.primary},${C.secondary})`, borderRadius: 18, padding: "16px 22px", marginBottom: 20, color: "#fff" }}>
          <div style={{ fontSize: 11, fontWeight: 700, opacity: .75, marginBottom: 8, textTransform: "uppercase" as any, letterSpacing: "0.08em" }}>Operational Intelligence</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" as any }}>
            {(assist.alerts || []).slice(0, 3).map((a: any, i: number) => (
              <div key={i} style={{ background: "rgba(255,255,255,0.15)", borderRadius: 12, padding: "9px 14px", flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>{a.title}</div>
                <div style={{ fontSize: 11, opacity: .85 }}>{a.message}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Tabs items={tabs} active={tab} onChange={setTab} />

      {tab === "Follow-ups" && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 18 }}>
            {["CRITICAL", "HIGH", "MEDIUM"].map(p => {
              const n = (fu || []).filter((f: any) => f.priority === p).length;
              return <KPI key={p} label={p} value={n} color={p === "CRITICAL" ? C.danger : p === "HIGH" ? C.warning : C.mid} icon={p === "CRITICAL" ? "🔴" : p === "HIGH" ? "🟡" : "🟢"} />;
            })}
          </div>
          {fuL ? [1, 2, 3].map(i => <Skeleton key={i} />) : !(fu || []).length ? <Empty icon="check" title="No follow-ups needed" desc="All customers are engaged" /> :
            (fu || []).map((f: any, i: number) => (
              <Card key={i} urgency={f.priority === "CRITICAL" ? C.danger : f.priority === "HIGH" ? C.warning : ""} style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <div><div style={{ fontSize: 15, fontWeight: 700 }}>{f.customerName}</div><div style={{ fontSize: 12, color: C.mid }}>+91 {f.phone}</div></div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}><Badge color={rC(f.priority)}>{f.priority}</Badge><span style={{ fontSize: 12, fontWeight: 700, color: C.success }}>{f.probability}%</span></div>
                </div>
                <div style={{ fontSize: 13, color: C.warning, fontWeight: 600, marginBottom: 4 }}>{f.reason}</div>
                <div style={{ fontSize: 12, color: C.mid, marginBottom: 10 }}>{f.recommendation}</div>
                <div style={{ background: C.bg, borderRadius: 8, padding: "8px 12px", fontSize: 12, lineHeight: 1.5, fontStyle: "italic", marginBottom: 10 }}>💬 "{(f.whatsappDraft || "").slice(0, 120)}…"</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <a href={waUrl(f.phone, f.whatsappDraft || "")} target="_blank" rel="noreferrer" style={{ background: "#25D366", color: "#fff", padding: "7px 14px", borderRadius: 9, fontSize: 12, fontWeight: 700, textDecoration: "none" }}>💬 Send WhatsApp</a>
                  <a href={`tel:+91${f.phone}`} style={{ background: C.bg, color: C.text, padding: "7px 12px", borderRadius: 9, fontSize: 12, fontWeight: 600, textDecoration: "none", border: `1px solid ${C.border}` }}>📞 Call</a>
                </div>
              </Card>
            ))
          }
        </div>
      )}

      {tab === "Revenue" && rev && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Card>
            <SH title="Revenue Forecast" />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
              {[{ l: "Today", v: fmt(rev.today?.forecastPaise || 0), c: C.primary }, { l: "This Month", v: fmt(rev.thisMonth?.forecastPaise || 0), c: C.text }].map(k => (
                <div key={k.l} style={{ background: C.bg, borderRadius: 10, padding: "12px 14px", textAlign: "center" as any }}>
                  <div style={{ fontSize: 10, color: C.light, fontWeight: 700, marginBottom: 4, textTransform: "uppercase" as any }}>{k.l}</div>
                  <div style={{ fontSize: 18, fontWeight: 900, color: k.c }}>{k.v}</div>
                </div>
              ))}
            </div>
            {rev.thisMonth?.vsLastMonth !== undefined && <div style={{ fontSize: 12, fontWeight: 700, color: rev.thisMonth.vsLastMonth >= 0 ? C.success : C.danger }}>{rev.thisMonth.vsLastMonth >= 0 ? "▲" : "▼"} {Math.abs(rev.thisMonth.vsLastMonth)}% vs last month</div>}
            {rev.churnRiskRevenuePaise > 0 && <div style={{ background: "#FDEAEA", borderRadius: 10, padding: 10, marginTop: 12, fontSize: 12, color: C.danger }}>⚠ <strong>{fmt(rev.churnRiskRevenuePaise)}</strong> at risk from overdue balances</div>}
          </Card>
          <Card>
            <SH title="Seasonal Intelligence" />
            {(rev.seasonalSpikes || []).map((s: any, i: number) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${C.bg}` }}>
                <div><div style={{ fontSize: 13, fontWeight: 600 }}>{s.month}</div><div style={{ fontSize: 11, color: C.mid }}>{s.reason}</div></div>
                <span style={{ fontSize: 14, fontWeight: 900, color: C.success }}>{s.expectedGrowth}</span>
              </div>
            ))}
          </Card>
        </div>
      )}

      {tab === "Risks" && (
        !(risks || []).length ? <Empty icon="check" title="No operational risks" desc="System running smoothly" /> :
          (risks || []).map((r: any, i: number) => (
            <Card key={i} urgency={rC(r.severity)} style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div style={{ fontSize: 15, fontWeight: 700 }}>{r.title}</div>
                <Badge color={rC(r.severity)}>{r.severity}</Badge>
              </div>
              <div style={{ fontSize: 13, color: C.mid, marginBottom: 8 }}>{r.description}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.primary }}>→ {r.recommendation}</div>
            </Card>
          ))
      )}

      {tab === "Staff" && (
        !(staff || []).length ? <Skeleton /> :
          (staff || []).map((s: any, i: number) => (
            <Card key={i} style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <div style={{ width: 40, height: 40, borderRadius: "50%", background: C.primary, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 16 }}>{s.staffName?.charAt(0)}</div>
                  <div><div style={{ fontSize: 14, fontWeight: 700 }}>{s.staffName}</div><div style={{ fontSize: 12, color: C.mid }}>{s.role}</div></div>
                </div>
                <div style={{ textAlign: "right" as any }}><div style={{ fontSize: 24, fontWeight: 900, color: s.performanceScore >= 80 ? C.success : s.performanceScore >= 60 ? C.warning : C.danger }}>{s.performanceScore}%</div></div>
              </div>
              <div style={{ height: 6, background: C.bg, borderRadius: 3, overflow: "hidden", marginBottom: 10 }}><div style={{ width: `${s.performanceScore}%`, height: "100%", background: s.performanceScore >= 80 ? C.success : s.performanceScore >= 60 ? C.warning : C.danger, borderRadius: 3, transition: "width .5s" }} /></div>
              {s.strengths?.[0] && <div style={{ fontSize: 12, color: C.success }}>✓ {s.strengths[0]}</div>}
              {s.trainingNeeds?.[0] && <div style={{ fontSize: 12, color: C.warning }}>⚠ {s.trainingNeeds[0]}</div>}
            </Card>
          ))
      )}

      {tab === "Feedback" && fbk && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Card>
            <SH title="Satisfaction" />
            <div style={{ textAlign: "center" as any, padding: "20px 0" }}>
              <div style={{ fontSize: 52, fontWeight: 900, color: C.primary }}>{fbk.overallSentiment}/5</div>
              <div style={{ fontSize: 13, color: C.mid, marginTop: 4 }}>from {fbk.totalFeedbacks} responses</div>
            </div>
            {(fbk.heatmap || []).map((h: any, i: number) => (
              <div key={i} style={{ marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}><span style={{ color: C.mid }}>{h.category}</span><span style={{ fontWeight: 700 }}>{h.score}</span></div>
                <div style={{ height: 6, background: C.bg, borderRadius: 3, overflow: "hidden" }}><div style={{ width: h.score, height: "100%", background: C.success, borderRadius: 3 }} /></div>
              </div>
            ))}
          </Card>
          <Card>
            <SH title="AI Recommendations" />
            {(fbk.aiRecommendations || []).map((r: string, i: number) => (
              <div key={i} style={{ display: "flex", gap: 10, padding: "10px 0", borderBottom: `1px solid ${C.bg}` }}><span style={{ fontSize: 16, flexShrink: 0 }}>💡</span><span style={{ fontSize: 13, lineHeight: 1.5 }}>{r}</span></div>
            ))}
          </Card>
        </div>
      )}
    </div>
  );
};

export default IntelDash;
